// Membership tab — "Download XLS": one workbook with everything, built in the browser (PR C′, 2026-09-22).
// Each sheet is fetched from GET /api/membership/export/{sheet} in pages of 2,000 rows so no call nears
// the SWA's 45-second cap; exceljs is imported on use (the same pattern as the Executive downloads).
// The file downloads through an anchor — never a popup.
//
// Variants (2026-10-23): the dropdown beside the button picks a layout.
//   default  the workbook as shipped on 2026-09-22
//   boddy1   "Dave Boddy's Variant 1" — the same workbook plus what Dave added to his copy of the
//            22 Oct 2026 download: a Category column on Payments (the membership's category code,
//            looked up from the Memberships sheet) and two summary sheets of payment amounts by year
//            and month, PIVOT_1 by payment type and PIVOT_2 by category.
import type ExcelJS from 'exceljs'
import { getJson, type Summary } from './api'

interface ExportPage { sheet: string; page: number; size: number; total: number; columns: string[]; rows: unknown[][] }

export const EXPORT_SHEETS: [string, string][] = [
  ['members', 'Members'],
  ['memberships', 'Memberships'],
  ['payments', 'Payments'],
  ['history', 'Historical changes'],   // "History" is a name Excel reserves
  ['discrepancies', 'Discrepancies'],
  ['pairings', 'Pairings'],
  ['changes', 'Change lines'],
]

export type MembershipExportVariant = 'default' | 'boddy1'
export const EXPORT_VARIANTS: { key: MembershipExportVariant; label: string; title: string }[] = [
  { key: 'default', label: 'Default', title: 'One Excel workbook with everything: every member, membership, payment, history row, discrepancy, pairing and change line, as at now, with a Read me sheet. About half a minute.' },
  { key: 'boddy1', label: "Dave Boddy's Variant 1", title: 'The default workbook plus a Category column on the Payments sheet (the membership category code from the Memberships sheet) and two summary sheets of payment amounts by year and month: PIVOT_1 by payment type, PIVOT_2 by category.' },
]

const PAGE = 2000
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
const ISO_MIDNIGHT = /T00:00:00(\.000)?Z$/
const GBP = '"£"#,##0'
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// An exported value becomes a cell: ISO date strings become real dates (date-only ones without a time),
// booleans Yes/No, everything else as it came.
function toCell(v: unknown): ExcelJS.CellValue {
  if (v === null || v === undefined) return null
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (typeof v === 'string' && ISO_DATE.test(v)) { const d = new Date(v); return Number.isNaN(d.getTime()) ? v : d }
  if (typeof v === 'number' || typeof v === 'string') return v
  return String(v)
}

// 1 → A, 27 → AA (Excel column letters for the lookup formula).
function colLetter(n: number): string {
  let s = ''
  for (let x = n; x > 0; x = Math.floor((x - 1) / 26)) s = String.fromCharCode(65 + ((x - 1) % 26)) + s
  return s
}

interface PayRow { year: string; month: number | null; type: string; cat: string; amount: number }

// A PIVOT_n sheet: payment amounts by year (every year, oldest first) and, under the latest year, by
// month — one column per key (payment type or category), a Grand Total column and row. Laid out the way
// Excel's pivot did it in Dave's copy, as plain values (filters re-applied by the reader if needed).
function fillPivotSheet(ws: ExcelJS.Worksheet, rows: PayRow[], keyOf: (r: PayRow) => string, filters: [string, string][]) {
  const keys = Array.from(new Set(rows.map(keyOf))).sort((a, b) => (a === '(blank)' ? 1 : b === '(blank)' ? -1 : a.localeCompare(b)))
  const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => (a === '(blank)' ? 1 : b === '(blank)' ? -1 : a.localeCompare(b)))
  const latest = [...years].reverse().find((y) => y !== '(blank)')
  const sum = (pick: (r: PayRow) => boolean) => {
    const by = new Map<string, number>()
    let total = 0
    for (const r of rows) if (pick(r)) { by.set(keyOf(r), (by.get(keyOf(r)) ?? 0) + r.amount); total += r.amount }
    return { by, total }
  }
  const money = (n: number | undefined): number | null => (n === undefined ? null : Math.round(n * 100) / 100)
  const line = (label: string, s: { by: Map<string, number>; total: number }, bold: boolean, indent = false) => {
    const r = ws.addRow([indent ? `   ${label}` : label, ...keys.map((k) => money(s.by.get(k))), money(s.total)])
    r.eachCell((c, i) => { if (i > 1) c.numFmt = GBP })
    if (bold) r.font = { bold: true }
    return r
  }

  let n = 1
  for (const [f, v] of filters) { ws.getCell(n, 1).value = f; ws.getCell(n, 2).value = v; n++ }
  if (filters.length) n++
  ws.getCell(n, 1).value = 'Sum of amount'; ws.getCell(n, 2).value = 'Column Labels'; n++
  const head = ws.getRow(n); head.values = ['Row Labels', ...keys, 'Grand Total']; head.font = { bold: true }; n++
  for (const y of years) {
    line(y, sum((r) => r.year === y), true)
    if (y === latest) for (let m = 0; m < 12; m++) {
      const s = sum((r) => r.year === y && r.month === m)
      if (s.by.size) line(MONTHS[m], s, false, true)
    }
  }
  line('Grand Total', sum(() => true), true)
  ws.getColumn(1).width = 14
  for (let i = 2; i <= keys.length + 2; i++) ws.getColumn(i).width = 13
  ws.views = [{ state: 'frozen', ySplit: n - 1 }]
}

export async function downloadMembershipWorkbook(summary: Summary | null, onProgress: (message: string) => void, variant: MembershipExportVariant = 'default'): Promise<void> {
  const ExcelJSlib = (await import('exceljs')).default
  const wb = new ExcelJSlib.Workbook()
  wb.creator = 'NYMR Reports — Membership tab'
  wb.created = new Date()
  const readme = wb.addWorksheet('Read me')
  const counts: [string, number][] = []
  const boddy = variant === 'boddy1'

  // Variant 1 working state: category by membership number (from Memberships), the two pivot sheets
  // (created straight after Memberships so they sit where Dave put them), the payment rows they summarise.
  const catByNumber = new Map<string, string>()
  let membershipsNumberCol = 0, membershipsCatCol = 0
  let pivot1: ExcelJS.Worksheet | null = null, pivot2: ExcelJS.Worksheet | null = null
  const payRows: PayRow[] = []

  for (const [key, label] of EXPORT_SHEETS) {
    let ws: ExcelJS.Worksheet | null = null
    let fetched = 0
    let total = 0
    let dateColumns = new Set<number>()
    let columns: string[] = []
    for (let page = 1; ; page++) {
      onProgress(`${label}: ${fetched.toLocaleString('en-GB')}${total ? ` of ${total.toLocaleString('en-GB')}` : ''} rows…`)
      const j = await getJson<ExportPage>(`/api/membership/export/${key}?page=${page}&size=${PAGE}`)
      if (!ws) {
        ws = wb.addWorksheet(label)
        columns = j.columns
        const cols = j.columns.map((c) => ({ header: c, key: c, width: Math.min(40, Math.max(12, c.length + 2)) }))
        if (boddy && key === 'payments') cols.push({ header: 'Category', key: 'Category', width: 12 })
        ws.columns = cols
        ws.getRow(1).font = { bold: true }
        if (boddy && key === 'payments') ws.getRow(1).getCell(cols.length).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } }
        ws.views = [{ state: 'frozen', ySplit: 1 }]
        if (cols.length) ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: cols.length } }
        dateColumns = new Set(j.columns.map((c, i) => (/(_at|_on|_date|^date_|_dob|paid_on|renewal|created|updated|refreshed|_since)/i.test(c) ? i : -1)).filter((i) => i >= 0))
        if (boddy && key === 'memberships') {
          membershipsNumberCol = j.columns.indexOf('membership_number') + 1
          membershipsCatCol = j.columns.indexOf('category_short') + 1
          pivot1 = wb.addWorksheet('PIVOT_1')
          pivot2 = wb.addWorksheet('PIVOT_2')
        }
      }
      total = j.total
      const iNumber = columns.indexOf('membership_number'), iCat = columns.indexOf('category_short')
      const iPaid = columns.indexOf('paid_on'), iAmount = columns.indexOf('amount'), iType = columns.indexOf('payment_type')
      for (const r of j.rows) {
        const cells = r.map(toCell)
        if (boddy && key === 'memberships' && iNumber >= 0 && iCat >= 0 && r[iNumber] != null) catByNumber.set(String(r[iNumber]), r[iCat] == null ? '' : String(r[iCat]))
        if (boddy && key === 'payments') {
          const number = iNumber >= 0 && r[iNumber] != null ? String(r[iNumber]) : ''
          const cat = (number && catByNumber.get(number)) || ''
          const rowNo = ws.rowCount + 1
          // The lookup Dave added (XLOOKUP in his copy), written as INDEX/MATCH so every Excel can open it;
          // the looked-up value travels as the cached result.
          const formula = iNumber >= 0 && membershipsNumberCol && membershipsCatCol
            ? `IFERROR(INDEX(Memberships!$${colLetter(membershipsCatCol)}:$${colLetter(membershipsCatCol)},MATCH(${colLetter(iNumber + 1)}${rowNo},Memberships!$${colLetter(membershipsNumberCol)}:$${colLetter(membershipsNumberCol)},0)),"")`
            : null
          cells.push(formula ? ({ formula, result: cat } as ExcelJS.CellValue) : cat)
          const paid = iPaid >= 0 && typeof r[iPaid] === 'string' ? (r[iPaid] as string) : ''
          const amount = iAmount >= 0 ? Number(r[iAmount] ?? 0) || 0 : 0
          const type = iType >= 0 && r[iType] != null && String(r[iType]) !== '' ? String(r[iType]) : '(blank)'
          payRows.push({ year: /^\d{4}/.test(paid) ? paid.slice(0, 4) : '(blank)', month: /^\d{4}-\d{2}/.test(paid) ? Number(paid.slice(5, 7)) - 1 : null, type, cat: cat || '(blank)', amount })
        }
        const row = ws.addRow(cells)
        // date-only values (midnight UTC) show as dd/mm/yyyy, timestamps with the time
        r.forEach((v, i) => {
          if (typeof v === 'string' && ISO_DATE.test(v)) row.getCell(i + 1).numFmt = ISO_MIDNIGHT.test(v) ? 'dd/mm/yyyy' : 'dd/mm/yyyy hh:mm'
          else if (dateColumns.has(i) && cells[i] instanceof Date) row.getCell(i + 1).numFmt = 'dd/mm/yyyy'
        })
      }
      fetched += j.rows.length
      if (j.rows.length < PAGE || fetched >= total) break
    }
    counts.push([label, fetched])
  }

  if (boddy && pivot1 && pivot2) {
    onProgress('Summarising payments…')
    fillPivotSheet(pivot1, payRows, (r) => r.type, [['gift_aid', '(All)'], ['Category', '(All)']])
    fillPivotSheet(pivot2, payRows, (r) => r.cat, [])
    counts.push(['PIVOT_1', pivot1.rowCount], ['PIVOT_2', pivot2.rowCount])
  }

  // Read me — what this is, when, how many, and what each source last said
  const now = new Date()
  readme.columns = [{ header: 'NYMR Membership — full export', key: 'a', width: 44 }, { header: '', key: 'b', width: 28 }, { header: '', key: 'c', width: 14 }, { header: '', key: 'd', width: 12 }, { header: '', key: 'e', width: 60 }]
  readme.getRow(1).font = { bold: true, size: 13 }
  readme.addRow(['Generated', now.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })])
  readme.addRow(['From', `reports.nymr.co.uk › Membership › Download XLS${boddy ? " › Dave Boddy's Variant 1" : ''}`])
  readme.addRow(['What it is', 'Every member, membership, payment, history row, discrepancy, pairing and change line held by the Membership system, as at the moment of download. Members and memberships are as last pulled from Ontrack (plus any edits made here); the sources below say when each was last refreshed.'])
  if (boddy) readme.addRow(['Variant 1', 'Payments carries a Category column (the membership category code, looked up from Memberships). PIVOT_1 sums payment amounts by year and month (months under the latest year) against payment type; PIVOT_2 the same against category. Both are plain values: filter the Payments sheet and re-total if you need a subset.'])
  readme.addRow([])
  const h = readme.addRow(['Sheet', 'Rows']); h.font = { bold: true }
  for (const [label, n] of counts) readme.addRow([label, n])
  readme.addRow([])
  const h2 = readme.addRow(['Source', 'Last refreshed (UTC)', 'Rows', 'Status', 'Note']); h2.font = { bold: true }
  for (const m of summary?.meta ?? []) readme.addRow([m.source, m.refreshed_at ? m.refreshed_at.replace('T', ' ').slice(0, 16) : 'never', m.row_count, m.status, m.message ?? ''])
  readme.addRow([])
  readme.addRow(['Notes', 'Dates are UTC. bigint keys (membership_number, person_number, history_id) arrive as text. History is append-only. Pairings are live ones only.'])

  onProgress('Building the workbook…')
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`
  a.href = url
  a.download = `NYMR-Membership-${stamp}${boddy ? '-V1' : ''}.xlsx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
